import { io, Socket } from "socket.io-client";
import {
  JoinRoomSuccessResult,
  ServerErrorResponse,
  ServerRoomIdResponse,
  ServerSuccessResponse,
} from "../interface/SocketResponse";
import { CardDTO, ChallengeAction, ChallengeStep, PlayerInfo, TeamId } from "../interface/GameTypes";
import { SocketEvent } from "../interface/SocketEvents";

export interface StartGameData {
  players: PlayerInfo[];
  numDecks: number;
  dealerIndex: number;
  matchDealIntroActive?: boolean;
  matchDealIntroState?: MatchDealIntroStateData | null;
  /** True when playing Literature (namespace `/literature`). */
  literature?: boolean;
  literatureScoringMode?: "standard" | "weighted";
  /** True when playing Rummy (namespace `/rummy`). */
  rummy?: boolean;
}

export interface CardsDealtData {
  hand: CardDTO[];
}

export interface BiddingStartData {
  currentBidderIndex: number;
  dealerIndex: number;
  redeal?: boolean;
  forcedBid?: boolean;
}

export interface BiddingUpdateData {
  bidderIndex: number;
  action: "Atta" | "Pass";
  nextBidderIndex: number;
  currentCallerIndex: number | null;
  /** True when this bid completed bidding (non-all-pass). */
  biddingComplete?: boolean;
  callerStake?: number;
  /** When bidding ended with a caller, the game enters the post–Atta challenge chain first. */
  awaitingChallenge?: boolean;
  challengeStep?: ChallengeStep;
  respondingTeam?: TeamId;
  /** True when the next bidder is the last human and must bid Atta */
  forcedBid?: boolean;
}

export interface ChallengeUpdateData {
  seatIndex: number;
  playerName: string;
  action: ChallengeAction;
  stake: number;
  /** Next challenge step if still in CHALLENGE; null when chain ended. */
  challengeStep: ChallengeStep | null;
  respondingTeam?: TeamId;
  trumpPickNext: boolean;
  /** Seat indices on the responding team that have Pass’d this step (unanimous decline). */
  passedSeatIndices?: number[];
}

export interface TrumpPickRequiredData {
  callerIndex: number;
  callerName: string;
  stake: number;
}

export interface TrumpPickedData {
  suit: string;
  callerIndex: number;
  callerTeam: TeamId;
  stake: number;
}

export interface TurnStartData {
  playerIndex: number;
  ledSuit: string | null;
  round: Array<{ playerIndex: number; card: { suit: string; rank: string } }>;
}

export interface CardPlayedData {
  playerIndex: number;
  card: { suit: string; rank: string };
  round: Array<{ playerIndex: number; card: { suit: string; rank: string } }>;
  nextPlayerIndex: number | null;
}

export interface RoundCompleteData {
  winnerIndex: number;
  winnerTeam: TeamId;
  round: Array<{ playerIndex: number; card: { suit: string; rank: string } }>;
  teamAMarks: number;
  teamBMarks: number;
  teamARoundWins: number;
  teamBRoundWins: number;
}

export interface MatchTeamPointsPayload {
  A: number;
  B: number;
}

/** Per-seat cumulative match net (server splits each deal's stake evenly within each team). */
export interface MatchPlayerNetPayload {
  uuid: string;
  name: string | null;
  seatIndex: number;
  teamId: TeamId;
  net: number;
}

export interface SetRecordPayload {
  teamAWins: number;
  teamBWins: number;
  draws: number;
  setSize: 3 | 5 | 7;
  matchesPlayed: number;
  setComplete: boolean;
}

export interface GameOverData {
  result: {
    callerTeam: TeamId;
    callerTeamMarks: number;
    oppositionMarks: number;
    stake: number;
    oppositionWon: boolean;
    totalMarks: number;
    winTarget: number;
    lastRoundBonus: number;
    trumpSuit: string | null;
    roundSummary: Array<{
      roundNumber: number;
      cards: Array<{ playerIndex: number; card: { suit: string; rank: string } }>;
      winnerIndex: number;
      winnerTeam: TeamId;
      points: number;
      isLastRound: boolean;
      lastRoundBonus: number;
    }>;
  };
  matchTeamPoints?: MatchTeamPointsPayload;
  matchPlayerPoints?: MatchPlayerNetPayload[];
  matchDealerSeat?: number;
  matchGamesPlayed?: number;
  setRecord?: SetRecordPayload;
}

export interface ProposedPlayer {
  uuid: string;
  name: string | null;
  seatIndex: number;
  teamId: TeamId;
  isBot: boolean;
}

export interface LobbyReadyData {
  players: ProposedPlayer[];
  hostUUID: string;
  maxPlayers?: number;
  matchTeamPoints?: MatchTeamPointsPayload;
  matchPlayerPoints?: MatchPlayerNetPayload[];
  matchGamesPlayed?: number;
  setRecord?: SetRecordPayload;
}

export interface TeamsUpdatedData {
  players: ProposedPlayer[];
  hostUUID: string;
  maxPlayers?: number;
  matchTeamPoints?: MatchTeamPointsPayload;
  matchPlayerPoints?: MatchPlayerNetPayload[];
  matchGamesPlayed?: number;
  setRecord?: SetRecordPayload;
}

export interface ReconnectSuccessData {
  roomId: string;
  playerUUID: string;
  gameStarted: boolean;
  players: PlayerInfo[] | null;
  proposedPlayers: ProposedPlayer[] | null;
  hostUUID?: string;
  myHand: CardDTO[];
  numDecks: number;
  dealerIndex: number;
  literature?: boolean;
  rummy?: boolean;
  maxPlayers?: number;
  matchTeamPoints?: MatchTeamPointsPayload;
  matchPlayerPoints?: MatchPlayerNetPayload[];
  matchGamesPlayed?: number;
  setRecord?: SetRecordPayload;
}

export interface LiteratureStateData {
  phase: "PLAYING" | "GAME_OVER";
  scoringMode: "standard" | "weighted";
  currentTurnSeat: number;
  teamScores: { A: number; B: number };
  claimedSets: Array<{ setId: string; scoringTeam: TeamId }>;
  lastAsk: {
    askerSeat: number;
    targetSeat: number;
    card: CardDTO;
    hit: boolean;
  } | null;
  winner: TeamId | null;
  myHand: CardDTO[];
  handSizes: number[];
  dealerIndex: number;
  unclaimedSets: string[];
  players: PlayerInfo[];
}

export interface LiteratureGameOverData {
  winner: TeamId;
  teamScores: { A: number; B: number };
}

export interface RummyTableMeldDTO {
  id: string;
  cards: CardDTO[];
}

/** Per-seat sync from server (`rummyState`), includes roster. */
export interface RummyStateData {
  phase: "playing" | "round_over";
  stockCount: number;
  discardTop: CardDTO | null;
  melds: RummyTableMeldDTO[];
  currentPlayerIndex: number;
  turnDrawn: boolean;
  initialMeldDone: boolean[];
  initialMeldPointsThisTurn: number;
  winnerIndex: number | null;
  stalemate: boolean;
  scores: number[];
  myHand: CardDTO[];
  handSizes: number[];
  players: PlayerInfo[];
}

export interface RummyRoundOverData {
  winnerIndex: number | null;
  stalemate: boolean;
  scores: number[];
}

export interface ReconnectFailedData {
  message: string;
}

export interface GameSnapshotData {
  phase: "BIDDING" | "CHALLENGE" | "TRUMP_PICK" | "PLAYING";
  dealerIndex: number;
  currentBidderIndex: number;
  callerIndex: number | null;
  trumpSuit: string | null;
  callerTeam: TeamId | null;
  challengeStep?: ChallengeStep | null;
  respondingTeam?: TeamId;
  /** During CHALLENGE: seats on the responding team that have Pass’d this step. */
  challengePasses?: number[];
  stake: number;
  currentTurnIndex: number | null;
  teamAMarks: number;
  teamBMarks: number;
  currentRound: Array<{ playerIndex: number; card: { suit: string; rank: string } }>;
  teamARoundWins: number;
  teamBRoundWins: number;
  ledSuit: string | null;
  matchTeamPoints?: MatchTeamPointsPayload;
  matchPlayerPoints?: MatchPlayerNetPayload[];
  matchDealerSeat?: number;
  matchGamesPlayed?: number;
  matchDealIntroActive?: boolean;
  matchTrailingTeam?: TeamId | null;
  trailingShuffleTapsByUuid?: Record<string, number>;
  matchDealIntroDeadlineMs?: number | null;
  setRecord?: SetRecordPayload;
}

export interface MatchDealIntroStateData {
  matchTeamPoints: MatchTeamPointsPayload;
  trailingTeam: TeamId;
  tapsByUuid: Record<string, number>;
  deadlineMs: number | null;
}

export interface MatchStateData {
  matchTeamPoints: MatchTeamPointsPayload;
  matchDealerSeat: number;
  matchGamesPlayed: number;
  matchPlayerPoints?: MatchPlayerNetPayload[];
  setRecord?: SetRecordPayload;
}

export interface SessionEndedData {
  setRecord: SetRecordPayload;
  teamTotals: MatchTeamPointsPayload;
  playerTotals: MatchPlayerNetPayload[];
}

export interface PlayerDisconnectedData {
  seatIndex: number;
  playerName: string | null;
}

export interface PlayerReconnectedData {
  seatIndex: number;
  playerName: string;
}

export interface ReceiveReactionData {
  eventId: string;
  fromPlayerName: string;
  fromPlayerUUID: string;
  reactionType: string;
  timestamp: number;
}

export interface ReactionAnnounceData {
  eventId: string;
  fromPlayerName: string;
  toPlayerName: string;
  reactionType: string;
  timestamp: number;
}

export interface ReactionRateLimitedData {
  retryAfterMs: number;
}

export interface TableGiphyPlayedData {
  eventId: string;
  fromPlayerName: string;
  fromPlayerUUID: string;
  giphyId: string;
  playUrl: string;
  timestamp: number;
}

export interface TableGiphyRateLimitedData {
  retryAfterMs: number;
}

export class Connection {
  private socket: Socket;

  constructor(url: string) {
    // Polling first, then upgrade — more reliable through CloudFront than websocket-only.
    this.socket = io(url, { transports: ["polling", "websocket"] });
  }

  listenForAvailableGames = (callback: (data: any) => void) => {
    this.socket.on(SocketEvent.AvailableGames, callback);
  };

  listenForConnectError = (callback: (err: Error) => void) => {
    this.socket.on("connect_error", callback);
  };

  removeConnectErrorListener = () => {
    this.socket.off("connect_error");
  };

  initPlayer = (name: string, id: string, authToken?: string | null) => {
    this.socket.emit(SocketEvent.Init, {
      name,
      id: id || undefined,
      ...(authToken ? { authToken } : {}),
    });
  };

  createRoom = (numberOfPlayers: number, botCount: number = 0) => {
    this.socket.emit(SocketEvent.CreateRoom, { numberOfPlayers, botCount });
  };

  joinRoom = (roomId: string) => {
    this.socket.emit(SocketEvent.JoinRoom, { roomId });
  };

  listenForRoomId = (callback: (data: ServerRoomIdResponse) => void) => {
    this.socket.on(SocketEvent.RoomID, callback);
  };

  listenForJoinRoomSuccess = (
    callback: (data: JoinRoomSuccessResult) => void
  ) => {
    this.socket.on(SocketEvent.JoinRoomSuccess, callback);
  };

  listenForStartGame = (callback: (data: StartGameData) => void) => {
    this.socket.on(SocketEvent.StartGame, callback);
  };

  listenForCardsDealt = (callback: (data: CardsDealtData) => void) => {
    this.socket.on(SocketEvent.CardsDealt, callback);
  };

  listenForPlayerJoining = (
    callback: (data: ServerSuccessResponse) => void
  ) => {
    this.socket.on(SocketEvent.PlayerJoined, callback);
  };

  listenForError = (callback: (data: ServerErrorResponse) => void) => {
    this.socket.on(SocketEvent.Error, callback);
  };

  placeBid = (action: "Atta" | "Pass") => {
    this.socket.emit(SocketEvent.Bid, { action });
  };

  challengeResponse = (action: ChallengeAction) => {
    this.socket.emit(SocketEvent.ChallengeResponse, { action });
  };

  listenForBiddingStart = (callback: (data: BiddingStartData) => void) => {
    this.socket.on(SocketEvent.BiddingStart, callback);
  };

  listenForBiddingUpdate = (callback: (data: BiddingUpdateData) => void) => {
    this.socket.on(SocketEvent.BiddingUpdate, callback);
  };

  listenForChallengeUpdate = (callback: (data: ChallengeUpdateData) => void) => {
    this.socket.on(SocketEvent.ChallengeUpdate, callback);
  };

  listenForTrumpPickRequired = (callback: (data: TrumpPickRequiredData) => void) => {
    this.socket.on(SocketEvent.TrumpPickRequired, callback);
  };

  pickTrump = (suit: string) => {
    this.socket.emit(SocketEvent.PickTrump, { suit });
  };

  acknowledgeTrump = () => {
    this.socket.emit(SocketEvent.TrumpAcknowledge);
  };

  listenForTrumpPicked = (callback: (data: TrumpPickedData) => void) => {
    this.socket.on(SocketEvent.TrumpPicked, callback);
  };

  listenForTurnStart = (callback: (data: TurnStartData) => void) => {
    this.socket.on(SocketEvent.TurnStart, callback);
  };

  playCard = (suit: string, rank: string) => {
    this.socket.emit(SocketEvent.PlayCard, { suit, rank });
  };

  listenForCardPlayed = (callback: (data: CardPlayedData) => void) => {
    this.socket.on(SocketEvent.CardPlayed, callback);
  };

  listenForRoundComplete = (callback: (data: RoundCompleteData) => void) => {
    this.socket.on(SocketEvent.RoundComplete, callback);
  };

  listenForGameOver = (callback: (data: GameOverData) => void) => {
    this.socket.on(SocketEvent.GameOver, callback);
  };

  listenForMatchDealIntroState = (callback: (data: MatchDealIntroStateData) => void) => {
    this.socket.on(SocketEvent.MatchDealIntroState, callback);
  };

  listenForMatchState = (callback: (data: MatchStateData) => void) => {
    this.socket.on(SocketEvent.MatchState, callback);
  };

  trailingShuffleDeckTap = () => {
    this.socket.emit(SocketEvent.TrailingShuffleDeckTap);
  };

  forfeitMatch = () => {
    this.socket.emit(SocketEvent.ForfeitMatch);
  };

  playAgain = (reshuffle: boolean) => {
    this.socket.emit(SocketEvent.PlayAgain, { reshuffle });
  };

  leaveRoom = () => {
    this.socket.emit(SocketEvent.LeaveRoom);
  };

  updateRoomCapacity = (maxPlayers: number) => {
    this.socket.emit(SocketEvent.UpdateRoomCapacity, { maxPlayers });
  };

  addBotsLobby = (count: number) => {
    this.socket.emit(SocketEvent.AddBotsLobby, { count });
  };

  removeBotsLobby = (count: number) => {
    this.socket.emit(SocketEvent.RemoveBotsLobby, { count });
  };

  addBotDisconnected = () => {
    this.socket.emit(SocketEvent.AddBotDisconnected);
  };

  hostKickPlayer = (targetPlayerUuid: string) => {
    this.socket.emit(SocketEvent.HostKickPlayer, { targetPlayerUuid });
  };

  listenForKickedFromRoom = (callback: (data: { reason?: string }) => void) => {
    this.socket.on(SocketEvent.KickedFromRoom, callback);
  };

  listenForLeftRoom = (callback: (data: { roomId: string }) => void) => {
    this.socket.on(SocketEvent.LeftRoom, callback);
  };

  literatureAsk = (targetSeat: number, suit: string, rank: string) => {
    this.socket.emit(SocketEvent.LiteratureAsk, { targetSeat, suit, rank });
  };

  literatureDeclare = (setId: string, assignments: Record<string, number>) => {
    this.socket.emit(SocketEvent.LiteratureDeclare, { setId, assignments });
  };

  listenForLiteratureState = (callback: (data: LiteratureStateData) => void) => {
    this.socket.on(SocketEvent.LiteratureState, callback);
  };

  listenForLiteratureGameOver = (callback: (data: LiteratureGameOverData) => void) => {
    this.socket.on(SocketEvent.LiteratureGameOver, callback);
  };

  rummyDraw = (source: "stock" | "discard") => {
    this.socket.emit(SocketEvent.RummyDraw, { source });
  };

  rummyLayMelds = (melds: CardDTO[][]) => {
    this.socket.emit(SocketEvent.RummyLayMelds, { melds });
  };

  rummyDiscard = (suit: string, rank: string) => {
    this.socket.emit(SocketEvent.RummyDiscard, { suit, rank });
  };

  listenForRummyState = (callback: (data: RummyStateData) => void) => {
    this.socket.on(SocketEvent.RummyState, callback);
  };

  listenForRummyRoundOver = (callback: (data: RummyRoundOverData) => void) => {
    this.socket.on(SocketEvent.RummyRoundOver, callback);
  };

  listenForLobbyReady = (callback: (data: LobbyReadyData) => void) => {
    this.socket.on(SocketEvent.LobbyReady, callback);
  };

  listenForTeamsUpdated = (callback: (data: TeamsUpdatedData) => void) => {
    this.socket.on(SocketEvent.TeamsUpdated, callback);
  };

  shuffleTeams = () => {
    this.socket.emit(SocketEvent.ShuffleTeams);
  };

  swapPlayers = (uuid1: string, uuid2: string) => {
    this.socket.emit(SocketEvent.SwapPlayers, { uuid1, uuid2 });
  };

  confirmStart = (setSize?: 3 | 5 | 7, literatureScoringMode?: "standard" | "weighted") => {
    this.socket.emit(SocketEvent.ConfirmStart, {
      ...(setSize ? { setSize } : {}),
      ...(literatureScoringMode ? { literatureScoringMode } : {}),
    });
  };

  listenForReconnect = (callback: () => void) => {
    this.socket.io.on("reconnect", callback);
  };

  removeReconnectListener = () => {
    this.socket.io.off("reconnect");
  };

  disconnect = () => {
    try {
      this.socket.disconnect();
    } catch {
      /* ignore */
    }
  };

  listenForReconnectSuccess = (callback: (data: ReconnectSuccessData) => void) => {
    this.socket.on(SocketEvent.ReconnectSuccess, callback);
  };

  listenForReconnectFailed = (callback: (data: ReconnectFailedData) => void) => {
    this.socket.on(SocketEvent.ReconnectFailed, callback);
  };

  endSession = () => {
    this.socket.emit(SocketEvent.EndSession);
  };

  listenForSessionEnded = (callback: (data: SessionEndedData) => void) => {
    this.socket.on(SocketEvent.SessionEnded, callback);
  };

  listenForGameSnapshot = (callback: (data: GameSnapshotData) => void) => {
    this.socket.on(SocketEvent.GameSnapshot, callback);
  };

  listenForPlayerDisconnected = (callback: (data: PlayerDisconnectedData) => void) => {
    this.socket.on(SocketEvent.PlayerDisconnected, callback);
  };

  listenForPlayerReconnected = (callback: (data: PlayerReconnectedData) => void) => {
    this.socket.on(SocketEvent.PlayerReconnected, callback);
  };

  sendReaction = (toPlayerUUID: string, reactionType: string, clientEventId: string) => {
    this.socket.emit(SocketEvent.SendReaction, { toPlayerUUID, reactionType, clientEventId });
  };

  listenForReceiveReaction = (callback: (data: ReceiveReactionData) => void) => {
    this.socket.on(SocketEvent.ReceiveReaction, callback);
  };

  listenForReactionAnnounce = (callback: (data: ReactionAnnounceData) => void) => {
    this.socket.on(SocketEvent.ReactionAnnounce, callback);
  };

  listenForReactionRateLimited = (callback: (data: ReactionRateLimitedData) => void) => {
    this.socket.on(SocketEvent.ReactionRateLimited, callback);
  };

  shareTableGiphy = (giphyId: string, clientEventId: string) => {
    this.socket.emit(SocketEvent.ShareTableGiphy, { giphyId, clientEventId });
  };

  listenForTableGiphyPlayed = (callback: (data: TableGiphyPlayedData) => void) => {
    this.socket.on(SocketEvent.TableGiphyPlayed, callback);
  };

  listenForTableGiphyRateLimited = (callback: (data: TableGiphyRateLimitedData) => void) => {
    this.socket.on(SocketEvent.TableGiphyRateLimited, callback);
  };

  removeListener = (event: string) => {
    this.socket.off(event);
  };
}
